import { Component, computed, effect, inject, input, OnInit, output, signal, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { DatetimeAdapter } from '@ng-matero/extensions/core';
import { MtxDatetimepickerMode } from '@ng-matero/extensions/datetimepicker';
import { MtxDatetimepickerType } from '@ng-matero/extensions/datetimepicker/datetimepicker-types';
import { DateTimePickerCustomHeaderComponent } from '@src/suite/common/controls/date-time-picker-custom-header/date-time-picker-custom-header';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';
import { selectClientTimeZone, selectPersonalTimeZone, selectPersonalTimeZoneDisplay } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { type Moment } from 'moment-timezone';

@Component({
    selector: 'st-date-time-picker',
    templateUrl: './date-time-picker.html',
    //TODO make it work with OnPush
    //changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./date-time-picker.scss'],
    host: {
        class: 'st-date-time-picker',
    },
})
export class DateTimePickerComponent extends EnpsControlComponent<DateTime> implements OnInit {
    dateTimeAdapter = inject(DatetimeAdapter<Moment>);
    min = input<DateTime>(new DateTime());
    onTimezoneChanged = output<DateTime>();
    clearLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebDatePickerClearLabel));
    cancelLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebCancelButtonLabel));
    applyLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebApplyButtonLabel));

    type: MtxDatetimepickerType = 'datetime';
    mode: MtxDatetimepickerMode = 'auto';
    touchUi = false;
    twelvehour = true;
    timeInterval = 5;
    timeInput = true;
    dateTimePickerCustomHeaderComponent = DateTimePickerCustomHeaderComponent;

    minDate = computed(() => {
        return this.min()?.moment || null;
    });
    personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    personalTimeZoneDisplay = this.store.selectSignal(selectPersonalTimeZoneDisplay);
    clientTimeZone = this.store.selectSignal(selectClientTimeZone);

    commonTimeZones = signal<Array<TimeZone>>([]);
    ianaTimeZones = signal<Array<TimeZone>>([]);
    currentTimeZone = computed(() => {
        if (this.internalValue()?.timeZone) {
            return this.internalValue().timeZone;
        }
        return this.personalTimeZone();
    });
    getTimeZone = computed(() => {
        return this.currentTimeZone()?.id;
    });
    internalValue = signal<DateTime>(new DateTime());
    internalFormControl: FormControl<Moment> = new FormControl(null);

    constructor() {
        super();
        effect(
            () => {
                const val = this.value();
                if (val && !val.timeZone && this.personalTimeZone().id !== this.currentTimeZone().id) {
                    const newDate = new Date(val?.get());
                    const convertedDate = DateTime.convertToTargetTZ(
                        new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), newDate.getHours(), newDate.getMinutes(), newDate.getSeconds()),
                        this.personalTimeZone().id,
                        this.currentTimeZone().id
                    );
                    const newInternalValue = new DateTime(convertedDate);
                    this.internalValue.set(newInternalValue || new DateTime());
                } else {
                    this.internalValue.set(val || new DateTime());
                }
                if (this.dateLocale()) {
                    this.dateTimeAdapter.setLocale(this.dateLocale());
                }
            },
            { allowSignalWrites: true }
        );
    }

    showTimeZone = computed(() => {
        return !this.asCustomField();
    });

    ngOnInit() {
        this.commonTimeZones.set(this.globalTablesService.CommonTimeZones);
        this.ianaTimeZones.set(this.globalTablesService.IanaTimeZones);
    }

    public dateTimeChanged(value: Moment) {
        if (value) {
            const currentDate = !this.internalValue() || this.internalValue()?.get()?.getFullYear() === 1800 ? new Date() : this.internalValue()?.get();
            let newDate = currentDate;
            if (!newDate) {
                newDate = new Date();
            }

            const newTime = value.toDate();
            newDate?.setFullYear(value.toDate().getFullYear());
            newDate?.setMonth(value.toDate().getMonth(), value.toDate().getDate());
            newDate.setHours(newTime.getHours());
            newDate.setMinutes(newTime.getMinutes());
            newDate.setSeconds(newTime.getSeconds());
            const newDateTime = new DateTime(newDate);
            this.changed.emit(newDateTime);
        } else {
            this.internalValue.update(val => {
                val.set(null);
                return val;
            });
            this.changed.emit(this.internalValue());
        }
    }

    dateValue = computed(() => {
        if (!this.internalValue()) return null;
        if (this.internalValue()?.get()?.getFullYear() === 1800) {
            return null;
        }
        return this.internalValue()?.moment;
    });

    public timezoneChanged(event: string) {
        const timeZone = [...this.commonTimeZones(), ...this.ianaTimeZones()].find(tz => tz.id === event);
        const currentDate = this.internalValue().get() || new Date();
        const newDate = DateTime.convertToTargetTZ(
            new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds()),
            this.personalTimeZone().id,
            timeZone.id
        );
        const newDateTime = new DateTime();
        newDateTime.set(newDate);
        this.onTimezoneChanged.emit(newDateTime);
        newDateTime.timeZone = timeZone;
    }

    _syncValueIfNecessary() {
        this.internalFormControl.setValue(this.dateValue());
    }

    _addValidatorIfNecessary() {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.markAllAsTouched();
    }

    _setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.internalFormControl.disable();
        } else {
            this.internalFormControl.enable();
        }
    }
}
